import { endOfDay, isSameDay, startOfDay } from 'date-fns';
import { FirestoreDataConverter, Timestamp } from 'firebase/firestore';
import uniqBy from 'lodash/uniqBy';
import { IconFormNames } from 'shared/assets/icons';
import { NewHabit } from 'shared/types/habit';
import {
  CompletionTargetUnit,
  FirebaseReturnSchedule,
  ScheduleEntry,
  ScheduleEntryState,
} from 'shared/types/habit-schedule';
import { NewTask, Task } from 'shared/types/task';
import { TaskType } from 'shared/types/task-base';

import { firebaseTimestampToDate } from './firebase-timestamp-to-date';

export const taskConverter: FirestoreDataConverter<Task> = {
  fromFirestore: (snapshot) => {
    const data = snapshot.data();

    const parsedData = {
      completedAt: firebaseTimestampToDate(data.completedAt),
      createdAt: firebaseTimestampToDate(data.createdAt) ?? new Date(),
      updatedAt: firebaseTimestampToDate(data.updatedAt) ?? new Date(),
      frozenAt: firebaseTimestampToDate(data.frozenAt),
      startDate: firebaseTimestampToDate(
        data.startDate ? data.startDate : data.createdAt,
      ),
      reminderTime: data.reminderTime ?? null,
      deadlineDeadline: firebaseTimestampToDate(data.deadlineDeadline),
      endStrategy: {
        deadline: firebaseTimestampToDate(data.endStrategy?.deadline),
        completionCount: data.endStrategy?.completionCount ?? null,
      },
      iconName: data.iconName ?? null,
      schedules: data.schedules,
    };

    if ([TaskType.Habit, TaskType.Repeating].includes(data.type)) {
      parsedData.iconName = data.iconName ?? IconFormNames.Repeat;
      parsedData.schedules = (data.schedules as FirebaseReturnSchedule[])?.map(
        (schedule) => ({
          ...schedule,
          startDate: firebaseTimestampToDate(schedule.startDate),
          endDate: firebaseTimestampToDate(schedule.endDate),
          entries: (
            [
              ...(schedule.entries?.map((entry) => ({
                ...entry,
                date: firebaseTimestampToDate(
                  entry.date as unknown as Timestamp,
                )!,
              })) ?? []),
              ...(schedule.completions?.map((date) =>
                schedule.entries?.find((entry) =>
                  isSameDay(
                    firebaseTimestampToDate(date)!,
                    firebaseTimestampToDate(
                      entry.date as unknown as Timestamp,
                    )!,
                  ),
                )
                  ? undefined
                  : {
                      id: `${firebaseTimestampToDate(date)!}`,
                      date: firebaseTimestampToDate(date)!,
                      state: ScheduleEntryState.Complete,
                      value: 1,
                    },
              ) ?? []),
              ...(schedule.skips?.map((date) =>
                schedule.entries?.find((entry) =>
                  isSameDay(
                    firebaseTimestampToDate(date)!,
                    firebaseTimestampToDate(
                      entry.date as unknown as Timestamp,
                    )!,
                  ),
                )
                  ? undefined
                  : {
                      id: `${firebaseTimestampToDate(date)!}`,
                      date: firebaseTimestampToDate(date)!,
                      state: ScheduleEntryState.Skip,
                      value: 1,
                    },
              ) ?? []),
            ] as ScheduleEntry[]
          )
            .filter(Boolean)
            .sort(
              (entryA, entryB) => entryA.date.getTime() - entryB.date.getTime(),
            ),
          completions:
            schedule.completions?.map((date) =>
              firebaseTimestampToDate(date),
            ) ?? [],
          skips:
            schedule.skips?.map((date) => firebaseTimestampToDate(date)) ?? [],
          plannedOccurrences: uniqBy(
            schedule.plannedOccurrences?.map((plannedOccurrence) => ({
              ...plannedOccurrence,
              date: firebaseTimestampToDate(
                plannedOccurrence.date as unknown as Timestamp,
              ),
            })) ?? [],
            // make sure a date is only once present in the planned occurrences
            ({ date }) => (date ? startOfDay(date).getTime() : null),
          ),
          completionTarget: {
            count: 1,
            unit: CompletionTargetUnit.Day,
            ...(schedule.completionTarget ?? {}),
          },
        }),
      );
    }

    return {
      ...data,
      ...parsedData,
    } as Task;
  },
  toFirestore: (task) => task,
};

export const newTaskConverter = (task: NewTask) => {
  const deadline = task.endStrategy?.deadline;

  return {
    ...task,
    type:
      task.type ??
      ((task as NewHabit).schedules ? TaskType.Repeating : TaskType.Task),
    iconName: (task as NewHabit).iconName ?? null,
    createdAt: new Date(),
    startDate: task.startDate ? new Date(task.startDate) : new Date(),
    duration: null,
    frozenAt: null,
    completedAt: null,
    archivedAt: null,
    goalId: task.goalId ?? null,
    lifeAreaId: task.lifeAreaId ?? null,
    reminderTime: task.reminderTime ?? null,
    endStrategy: {
      deadline: deadline ? endOfDay(deadline) : null,
      completionCount: task.endStrategy?.completionCount ?? null,
    },
    schedules: task.schedules?.length
      ? task.schedules.map((schedule) => ({
          ...schedule,
          startDate: schedule.startDate
            ? new Date(schedule.startDate)
            : new Date(),
          endDate: schedule.endDate ? new Date(schedule.endDate) : null,
          entries: schedule.entries?.map((entry) => ({
            ...entry,
            date: entry.date ? new Date(entry.date) : null,
          })),
        }))
      : null,
  };
};
